import { useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useLiveMarketQuotes } from 'hooks/useLiveMarketQuotes';
import { useHasExpandedSubscriptions, useIsPageVisible } from 'hooks/useStagedSubscriptions';
import {
    applyLiveMarketDataToEntities,
    applyLiveMarketDataToEntity,
    isMarketDetermined,
    marketInverseTicker,
    type EventEntity,
    type MarketEntity,
    type SeriesEntity,
} from 'queries/marketEntityTransform';
import { useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';

const NO_TICKERS: string[] = [];
const NO_MARKET_TICKERS: string[] = [];

interface UseLiveMarketEntityOptions {
    /**
     * Caller-side eligibility, ANDed with this hook's own gates. Pass `false` for markets that can
     * never receive ticker frames — market-builder drafts, for one.
     */
    enabled?: boolean;
}

interface LiveMarketEntity {
    market: MarketEntity;
    eventMarkets: MarketEntity[];
}

interface UseLiveEventMarketsOptions {
    /**
     * Subscribe only these tickers instead of every market in the array — for surfaces that render
     * the full set but only display a subset's prices (`EventPriceHistoriesGraph`'s top-market
     * chips + checked strikes on a full-field event). Markets outside the subset pass through the
     * overlay unchanged at REST cadence. Must be derived from BASE (REST-owned) data, never from
     * the overlaid result, so a tick cannot change the subscription set that produced it — and it
     * should hold a stable reference across unrelated renders (memoize or key it) so
     * `useLiveMarketQuotes` can skip rebuilding its content key.
     */
    onlyTickers?: readonly string[];
}

/**
 * Subscribes a SET of markets and returns them with live quotes + lifecycle status overlaid — for
 * consumers that read an aggregate rather than a single card's price (`eventVolume`,
 * `eventHasOpenMarkets`, `eventMean`, the chart header's per-market probabilities).
 *
 * Prefer `useLiveEventMarketsForEvent` when the set is simply "this event's markets" — it owns the
 * store read too. This lower-level form is for callers that already hold the array.
 *
 * `deferralKey` scopes the staging window; pass the event ticker so a client-side event switch
 * re-arms it. Returns the identical array reference when nothing changed.
 */
export function useLiveEventMarkets(
    markets: MarketEntity[],
    deferralKey: string,
    options: UseLiveEventMarketsOptions = {},
): MarketEntity[] {
    const isPageVisible = useIsPageVisible();
    const hasExpandedSubscriptions = useHasExpandedSubscriptions(deferralKey);
    const enabled = hasExpandedSubscriptions && isPageVisible;

    // Keyed on the BASE array's identity, which is tick-stable (it comes from the REST-owned store
    // or props, not from an overlay). `useLiveMarketQuotes` tolerates a fresh array per render, but
    // it can only skip rebuilding its sorted content key when the reference holds — and for a
    // full-field event whose chart wrapper re-renders on every tick, that rebuild is an O(n log n)
    // string sort per tick.
    const allTickers = useMemo(() => markets.map((market) => market.ticker), [markets]);
    const tickers = options.onlyTickers ?? allTickers;

    const liveData = useLiveMarketQuotes(enabled ? tickers : NO_TICKERS);

    // Memoized, not called per render: the overlay only preserves identity when it's a NO-OP, and
    // between REST polls a subscribed quote almost always diverges from the base entity — the
    // steady state. Unmemoized, every render from any cause (a hover scrub, a strike toggle) would
    // hand the caller a fresh array + fresh entities, invalidating every `markets`-keyed memo and
    // turning `markets`-keyed effects that set state into render loops. `liveData` is
    // reference-stable across unrelated renders (see `useLiveMarketQuotes`), so this only
    // recomputes on an actual tick or base change.
    return useMemo(() => applyLiveMarketDataToEntities(markets, liveData), [markets, liveData]);
}

/**
 * `useLiveEventMarkets` for the common case: every market of `eventTicker`, read from
 * `marketEntitiesStore` and overlaid with live data. Owns the store read so the select → filter →
 * overlay block doesn't have to be repeated (and kept in sync) at every aggregate consumer.
 *
 * Pass `undefined` to opt out entirely — callers that must not read the store during SSR / first
 * paint (see the `store ?? query` note in AGENTS.md) can gate on their own `isClient` flag.
 *
 * The returned array is reference-stable while the underlying entities and quotes are: callers put
 * it straight into `useMemo` deps and effect deps.
 */
export function useLiveEventMarketsForEvent(
    eventTicker: string | undefined,
    options: UseLiveEventMarketsOptions = {},
): MarketEntity[] {
    const marketTickers =
        useMarketEntitiesStore((s) => (eventTicker ? s.events[eventTicker]?.marketTickers : undefined)) ??
        NO_MARKET_TICKERS;
    const marketsByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(marketTickers.map((ticker) => [ticker, s.markets[ticker]]))),
    );

    // Must be memoized: an inline `.map().filter()` produces a new array every render, which makes
    // each caller's derived `visibleMarkets` a new reference every render. Where one of those feeds
    // an effect that sets state (`SlideChartWidget`'s binary-publish effect → `onColorsCalculated` →
    // `setChartDisplayState`) that is an endless render loop — React's "Maximum update depth
    // exceeded". `marketsByTicker` is already `useShallow`-stable per ticker.
    const markets = useMemo(
        () => marketTickers.map((ticker) => marketsByTicker[ticker]).filter((m): m is MarketEntity => m !== undefined),
        [marketTickers, marketsByTicker],
    );

    return useLiveEventMarkets(markets, eventTicker ?? '', options);
}

/**
 * Multi-event form of `useLiveEventMarketsForEvent`: reads every market of `eventTickers` from
 * `marketEntitiesStore`, subscribes them as ONE flat set under `deferralKey`, and returns them
 * grouped per event with live data overlaid.
 *
 * Exists for multi-event surfaces (hub heroes, featured-prop rows) that would otherwise select
 * nested arrays from the store: a `useShallow` selector whose record values are freshly-built
 * `MarketEntity[]`s is NEVER shallow-equal (`shallow` compares values with `Object.is`), so every
 * store write — including this surface's own poll — would hand back a new record and rebuild every
 * downstream memo with zero data change. Selecting flat ticker→entity records keeps per-entity
 * identity, which REST hydration preserves across content-equal polls.
 */
export function useLiveMarketsByEvent(
    eventTickers: readonly string[],
    deferralKey: string,
): Record<string, MarketEntity[]> {
    // Content-keyed like `useLiveMarketQuotes`, so callers can build the ticker array inline.
    const eventTickersKey = eventTickers.join('|');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableEventTickers = useMemo(() => eventTickers, [eventTickersKey]);

    // Both selections are flat records whose values are store-owned references (`marketTickers`
    // arrays survive content-equal hydrations via the store's `replaceEqualDeep`), so `useShallow`
    // actually holds across unrelated writes.
    const marketTickersByEvent = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(stableEventTickers.map((t) => [t, s.events[t]?.marketTickers]))),
    );
    const allMarketTickers = useMemo(
        () => stableEventTickers.flatMap((t) => marketTickersByEvent[t] ?? []),
        [stableEventTickers, marketTickersByEvent],
    );
    const marketsByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(allMarketTickers.map((ticker) => [ticker, s.markets[ticker]]))),
    );

    const baseMarketsByEvent = useMemo(
        () =>
            Object.fromEntries(
                stableEventTickers.map((t) => [
                    t,
                    (marketTickersByEvent[t] ?? [])
                        .map((ticker) => marketsByTicker[ticker])
                        .filter((m): m is MarketEntity => m !== undefined),
                ]),
            ),
        [stableEventTickers, marketTickersByEvent, marketsByTicker],
    );
    const baseFlatMarkets = useMemo(
        () => stableEventTickers.flatMap((t) => baseMarketsByEvent[t]),
        [stableEventTickers, baseMarketsByEvent],
    );

    const liveFlatMarkets = useLiveEventMarkets(baseFlatMarkets, deferralKey);

    return useMemo(() => {
        // Overlay was a no-op → keep the grouped record's identity too.
        if (liveFlatMarkets === baseFlatMarkets) return baseMarketsByEvent;
        const liveByTicker = new Map(liveFlatMarkets.map((m) => [m.ticker, m]));
        return Object.fromEntries(
            Object.entries(baseMarketsByEvent).map(([t, ms]) => [t, ms.map((m) => liveByTicker.get(m.ticker) ?? m)]),
        );
    }, [baseMarketsByEvent, baseFlatMarkets, liveFlatMarkets]);
}

/**
 * Subscribes ONE market — for consumers that hold a single market with no event context to
 * normalize against (a portfolio row, for one) and so need neither the inverse-ticker resolution of
 * `useLiveMarketEntity` nor the array plumbing of `useLiveEventMarkets`.
 *
 * `deferralKey` should be scoped to the surface, not to the row: a per-row key would give every row
 * its own staging timer and expansion entry, which is the boot cost `useStagedSubscriptions`
 * exists to avoid.
 */
export function useLiveMarket(market: MarketEntity | undefined, deferralKey: string): MarketEntity | undefined {
    const isPageVisible = useIsPageVisible();
    const hasExpandedSubscriptions = useHasExpandedSubscriptions(deferralKey);
    // Same determined gate as `useLiveMarketEntity`, and for the same anti-oscillation reason it
    // reads the caller's REST-owned entity, never the overlay: a determined market's book can't
    // move, so holding its subscription open would only pay snapshot + refcount cost.
    const enabled = !!market && !isMarketDetermined(market) && hasExpandedSubscriptions && isPageVisible;

    const ticker = market?.ticker;
    const tickers = useMemo(() => (enabled && ticker ? [ticker] : NO_TICKERS), [enabled, ticker]);

    const liveData = useLiveMarketQuotes(tickers);

    // Memoized for the same reason as `useLiveEventMarkets`: while the quote diverges from the
    // REST base (the steady state), an unmemoized overlay would allocate a fresh entity per render.
    // The per-ticker `liveData` entry is stable while the store's quote/status entries are.
    const overlay = market ? liveData[market.ticker] : undefined;
    return useMemo(() => market && applyLiveMarketDataToEntity(market, overlay), [market, overlay]);
}

/**
 * Subscribes ONE card to its own market's live ticker quote + lifecycle status and returns the
 * market with that data overlaid. This is how live prices reach the UI now that
 * `marketEntitiesStore` is REST-owned: a WS tick re-renders only the cards that subscribed to the
 * ticker it names, instead of invalidating a page-level market array and cascading through every
 * parent between the store and the tile.
 *
 * Also returns `eventMarkets`, overlaid only when it matters — see the inverse-ticker note below.
 * Callers should shadow both props with these values so every derived display in the component
 * reads one consistent snapshot.
 */
export function useLiveMarketEntity(
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'ticker' | 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
    options: UseLiveMarketEntityOptions = {},
): LiveMarketEntity {
    const isPageVisible = useIsPageVisible();
    const hasExpandedSubscriptions = useHasExpandedSubscriptions(event.ticker);

    // Gate on the BASE entity's determined state, never the overlaid one. Reading the overlay here
    // would oscillate: overlay reports determined -> gate closes -> the watcher's `onTickersDropped`
    // deletes the cached status -> the overlay falls back to the still-active REST entity -> gate
    // reopens. The REST-owned value only changes on a poll, so it can't feed back into itself.
    // Deliberately not gated on `isMarketClosed`, which compares `closeDate` against a
    // non-reactive `Date.now()` and so could never re-arm.
    const enabled =
        (options.enabled ?? true) && !isMarketDetermined(market) && hasExpandedSubscriptions && isPageVisible;

    // For events with at most two markets, the displayed probability and delta are normalized
    // through the INVERSE market's price (see `marketNormalizedLastValidYesPrice`), so subscribing
    // only our own ticker would leave a live CTA price next to a stale percentage on every
    // two-outcome game tile. Resolved from the BASE markets for the same anti-oscillation reason as
    // above: `resolveInverseTicker` inspects last price and volume, so resolving it from overlaid
    // values would let a tick change the subscription set that produced it.
    const inverseTicker = useMemo(
        () => marketInverseTicker(market, eventMarkets, event, series),
        [market, eventMarkets, event, series],
    );

    const tickers = useMemo(() => {
        if (!enabled) return NO_TICKERS;
        return inverseTicker ? [market.ticker, inverseTicker] : [market.ticker];
    }, [enabled, market.ticker, inverseTicker]);

    const liveData = useLiveMarketQuotes(tickers);

    // Memoized for the same reason as `useLiveEventMarkets`: while a quote diverges from the REST
    // base (the steady state), an unmemoized overlay would allocate fresh entities — and a fresh
    // result object — on every render, defeating every `market`/`eventMarkets`-keyed memo
    // downstream.
    return useMemo(() => {
        const liveMarket = applyLiveMarketDataToEntity(market, liveData[market.ticker]);
        // Only rebuild the array when an inverse is actually in play — otherwise every consumer's
        // `eventMarkets`-keyed memo would invalidate on each tick for no benefit.
        const liveEventMarkets = inverseTicker ? applyLiveMarketDataToEntities(eventMarkets, liveData) : eventMarkets;

        return { market: liveMarket, eventMarkets: liveEventMarkets };
    }, [market, eventMarkets, inverseTicker, liveData]);
}
